import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useEffect, useMemo, useState } from 'react';

import { COLORS } from './KubernetesPage';

/** The subset of a kind definition the picker needs. */
export interface PickableKind {
  id: string;
  label: string;
  kind: string;
  group: string;
  plural: string;
  namespaced?: boolean;
  discovered?: boolean;
}

/** Rows rendered per frame — a cluster can serve several hundred kinds. */
const VISIBLE_LIMIT = 60;

/**
 * Searchable list of every resource type the cluster serves.
 *
 * A discovery-backed console knows about hundreds of kinds, which is far too
 * many for a sidebar. The curated sections stay in the nav for the kinds
 * people use daily; this is how they reach everything else — an operator's
 * CRDs, ASO types, anything installed since.
 */
export function KindPicker({
  open,
  kinds,
  loading,
  error,
  onClose,
  onSelect,
}: {
  open: boolean;
  kinds: PickableKind[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (kind: PickableKind) => void;
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return kinds;
    return kinds.filter(kind =>
      `${kind.kind} ${kind.group} ${kind.plural}`.toLowerCase().includes(term),
    );
  }, [kinds, query]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>All resource types</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={query}
          placeholder="Kind, API group, or plural name"
          onChange={event => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 0.5, mb: 1.5 }}
        />

        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Could not read the cluster's resource catalogue — only the curated
            kinds are listed. {error}
          </Alert>
        )}

        {loading && !kinds.length ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ color: COLORS.muted, fontSize: 12.5 }}>
              Reading the cluster's resource catalogue…
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography sx={{ color: COLORS.muted, fontSize: 11.5, mb: 1 }}>
              {matches.length} of {kinds.length} resource types
              {matches.length > VISIBLE_LIMIT &&
                ` · showing the first ${VISIBLE_LIMIT}`}
            </Typography>
            <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
              {matches.slice(0, VISIBLE_LIMIT).map(kind => (
                <Box
                  key={kind.id}
                  component="button"
                  onClick={() => onSelect(kind)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.85,
                    border: 0,
                    borderBottom: `1px solid ${COLORS.border}`,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { bgcolor: '#ffffff0a' },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}
                    >
                      {kind.kind}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ color: COLORS.muted, fontSize: 11 }}
                    >
                      {kind.group || 'core'} · {kind.plural}
                    </Typography>
                  </Box>
                  {!kind.namespaced && (
                    <Chip
                      size="small"
                      label="Cluster"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        color: COLORS.muted,
                        border: `1px solid ${COLORS.border}`,
                        bgcolor: 'transparent',
                      }}
                    />
                  )}
                  {!kind.discovered && (
                    <Chip
                      size="small"
                      label="Curated"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        color: COLORS.blue,
                        border: `1px solid ${COLORS.blue}55`,
                        bgcolor: `${COLORS.blue}12`,
                      }}
                    />
                  )}
                </Box>
              ))}
              {!matches.length && (
                <Typography
                  sx={{ color: COLORS.muted, fontSize: 12.5, py: 2 }}
                >
                  No resource type matches “{query}”.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
